import { Loader2, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { languageLabel, TRANSLATION_DISCLAIMER } from '@/lib/languages';
import type { DocumentTranslation } from '@/hooks/useDocumentTranslation';

interface Props {
  data: DocumentTranslation | null;
  loading: boolean;
  error: string | null;
  targetLanguage: string;
}

/**
 * Renders the view-only whole-document translation beside the source PDF:
 * a translated "Key information" sheet followed by the full contract text.
 * Purely presentational — the parent SourcePdfCard owns the fetch/state.
 */
export function SourceTranslationPane({ data, loading, error, targetLanguage }: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-md border border-dashed py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Translating to {languageLabel(targetLanguage)}…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="py-2">
        <AlertDescription className="text-xs">{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-12 text-center text-muted-foreground">
        <Info className="h-5 w-5" aria-hidden="true" />
        <p className="text-xs max-w-[24ch]">
          Pick a language and press Translate to read this contract and its
          extracted info here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-muted-foreground">
        {data.source_language} → {languageLabel(data.target_language)}
        {data.input_tokens != null &&
          data.output_tokens != null &&
          ` · ${data.input_tokens}+${data.output_tokens} tokens`}
      </p>

      {data.info_text && (
        <section>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Key information
          </p>
          <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
            {data.info_text}
          </pre>
        </section>
      )}

      {data.full_text && (
        <section>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Full document
          </p>
          <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
            {data.full_text}
          </pre>
        </section>
      )}

      <p className="text-[10px] italic text-muted-foreground">
        {TRANSLATION_DISCLAIMER}
      </p>
    </div>
  );
}
